import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import { TeamMemberTaskStatus } from "@fitness-app/data-models/entities/TeamMemberTask";

import { useUserClaims } from "~/hooks/trainer/useUserClaims";
import { useUserRole } from "~/hooks/trainer/useUserRole";
import { supabase } from "~/store/initializeStore";

export const TEAM_MEMBER_TASKS_COUNT_QUERY_KEY = "team_member_tasks_count";

interface TaskCounts {
  today: number;
  overdue: number;
  total: number;
}

export const useTeamMemberTasksCount = () => {
  const { userId, isTeamMember } = useUserRole();
  const { memberId } = useUserClaims();

  const { data, isLoading, error } = useQuery({
    queryKey: [TEAM_MEMBER_TASKS_COUNT_QUERY_KEY, userId],
    enabled: Boolean(userId) && isTeamMember && Boolean(memberId),
    refetchInterval: 60000, // Refetch every minute while window is focused
    refetchIntervalInBackground: false,
    queryFn: async () => {
      const today = dayjs().format("YYYY-MM-DD");

      // Fetch only pending/in_progress tasks with minimal data
      const { data, error } = await supabase
        .from("team_member_task")
        .select("id, dueDate, status")
        .eq("assignedTeamMemberId", memberId!)
        .in("status", [TeamMemberTaskStatus.Pending, TeamMemberTaskStatus.InProgress]);

      if (error) {
        throw error;
      }

      const tasks = data || [];

      let todayCount = 0;
      let overdueCount = 0;

      tasks.forEach((task) => {
        const dueDate = (task.dueDate as { date: string | null } | null)?.date;
        if (!dueDate) return;

        if (dueDate === today) {
          todayCount++;
        } else if (dueDate < today) {
          overdueCount++;
        }
      });

      return {
        today: todayCount,
        overdue: overdueCount,
        total: todayCount + overdueCount,
      } satisfies TaskCounts;
    },
  });

  return {
    counts: data || { today: 0, overdue: 0, total: 0 },
    isLoading,
    error,
  };
};
